import {GatewayType, isSameModuleType} from '../interfaces/juconnGateway.response';
import {UvsModuleModel} from '../models/uvs-module.model';
import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from "rxjs";
import {catchError, delay, filter, map, skip, skipUntil, switchMap, take, tap, withLatestFrom} from "rxjs/operators";
import {AutoSaveService} from "./auto-save.service";
import {DeviceDataService} from "./device-data.service";
import {MqttBlockingService} from "./mqtt-blocking.service";
import {AlertService} from "./alert.service";
import {DataService} from "./data.service";
import {IInstallationDataResponse} from "../interfaces/installation-data.response";
import {BuildingModel} from "../models/data-models/building-model";
import {GeneralGatewayModel} from "../models/data-models/general-gateway-model";
import {GeneralMeasurementModel} from "../models/data-models/general-measurement-model";
import {GeneralPrePhotosModel} from "../models/data-models/general-pre-photos-model";
import {GeneralSendingModel} from "../models/data-models/general-sending-model";
import {HeatingCheckModel} from "../models/data-models/heating-check-model";
import {HeatingInformationModel} from "../models/data-models/heating-information-model";
import {PhotosModel} from "../models/data-models/photos-model";
import {SensorsCounterModel} from "../models/data-models/sensors-counter-model";
import {SensorsOtherModel} from "../models/data-models/sensors-other-model";
import {SensorsOutsideTempModel} from "../models/data-models/sensors-outside-temp-model";
import {SensorsPumpModel} from "../models/data-models/sensors-pump-model";
import {SensorsTemperatureModel} from "../models/data-models/sensors-temperature-model";
import {Utils} from "../utils/utils";
import {Customer} from "../models/customer-model";
import {Router} from "@angular/router";
import {MeterTypeEnum} from "../models/meter-type.enum";
import {IRMeterTypeEnum} from "../models/ir-meter-type.enum";

@Injectable({
  providedIn: 'root'
})
export class ShareService implements OnDestroy {
  public debugMode = false;

  private readonly gatewayIdSubject: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  public readonly gatewayId$: Observable<string> = this.gatewayIdSubject.asObservable();

  public simIp?: string;
  public signalQuality?: string;
  public radioType?: string;
  public version?: string;

  private readonly buildingSubject: BehaviorSubject<BuildingModel> = new BehaviorSubject<BuildingModel>(undefined);
  public readonly building$: Observable<BuildingModel> = this.buildingSubject.asObservable();

  private readonly generalGatewaySubject: BehaviorSubject<GeneralGatewayModel> = new BehaviorSubject<GeneralGatewayModel>(undefined);
  public readonly generalGateway$: Observable<GeneralGatewayModel> = this.generalGatewaySubject.asObservable();

  private readonly generalMeasurementSubject: BehaviorSubject<GeneralMeasurementModel> = new BehaviorSubject<GeneralMeasurementModel>(undefined);
  public readonly generalMeasurement$: Observable<GeneralMeasurementModel> = this.generalMeasurementSubject.asObservable();

  private readonly generalPrePhotosSubject: BehaviorSubject<GeneralPrePhotosModel> = new BehaviorSubject<GeneralPrePhotosModel>(undefined);
  public readonly generalPrePhotos$: Observable<GeneralPrePhotosModel> = this.generalPrePhotosSubject.asObservable();

  private readonly generalSendingSubject: BehaviorSubject<GeneralSendingModel> = new BehaviorSubject<GeneralSendingModel>(undefined);
  public readonly generalSending$: Observable<GeneralSendingModel> = this.generalSendingSubject.asObservable();

  private readonly heatingCheckSubject: BehaviorSubject<HeatingCheckModel> = new BehaviorSubject<HeatingCheckModel>(undefined);
  public readonly heatingCheck$: Observable<HeatingCheckModel> = this.heatingCheckSubject.asObservable();

  private readonly heatingInformationSubject: BehaviorSubject<HeatingInformationModel> = new BehaviorSubject<HeatingInformationModel>(undefined);
  public readonly heatingInformation$: Observable<HeatingInformationModel> = this.heatingInformationSubject.asObservable();

  private readonly photosSubject: BehaviorSubject<PhotosModel> = new BehaviorSubject<PhotosModel>(undefined);
  public readonly photos$: Observable<PhotosModel> = this.photosSubject.asObservable();

  private readonly sensorsCounterSubject: BehaviorSubject<SensorsCounterModel> = new BehaviorSubject<SensorsCounterModel>(undefined);
  public readonly sensorsCounter$: Observable<SensorsCounterModel> = this.sensorsCounterSubject.asObservable();

  private readonly sensorsOtherSubject: BehaviorSubject<SensorsOtherModel> = new BehaviorSubject<SensorsOtherModel>(undefined);
  public readonly sensorsOther$: Observable<SensorsOtherModel> = this.sensorsOtherSubject.asObservable();

  private readonly sensorsOutsideTempSubject: BehaviorSubject<SensorsOutsideTempModel> = new BehaviorSubject<SensorsOutsideTempModel>(undefined);
  public readonly sensorsOutsideTemp$: Observable<SensorsOutsideTempModel> = this.sensorsOutsideTempSubject.asObservable();

  private readonly sensorsPumpSubject: BehaviorSubject<SensorsPumpModel> = new BehaviorSubject<SensorsPumpModel>(undefined);
  public readonly sensorsPump$: Observable<SensorsPumpModel> = this.sensorsPumpSubject.asObservable();

  private readonly sensorsTemperatureSubject: BehaviorSubject<SensorsTemperatureModel> = new BehaviorSubject<SensorsTemperatureModel>(undefined);
  public readonly sensorsTemperature$: Observable<SensorsTemperatureModel> = this.sensorsTemperatureSubject.asObservable();

  public isMqttBlocked = false;

  private readonly customerOptionsSubject: BehaviorSubject<Customer[]> = new BehaviorSubject<Customer[]>([]);
  public readonly customerOptions$: Observable<Customer[]> = this.customerOptionsSubject.asObservable();

  private subscriptions: Subscription[] = [];

  constructor(
    private autoSaveService: AutoSaveService,
    private deviceDataService: DeviceDataService,
    private mqttBlockingService: MqttBlockingService,
    private alertService: AlertService,
    private dataService: DataService,
    private router: Router
  ) {
    this.subscriptions.push(combineLatest(this.mqttBlockingService.getBlockedGateways(), this.gatewayId$).subscribe(([gateways, gatewayId]) => {
      if (gatewayId) {
        this.isMqttBlocked = gateways.includes(gatewayId);
      }
    }));

    this.subscriptions.push(
      this.gatewayId$.pipe(
        skip(1),
        switchMap(gatewayId => this.buildGatewayLoadPipeline(gatewayId))
      ).subscribe()
    );

    this.subscriptions.push(this.deviceDataService.moduleConfig$
      .pipe(
        filter(moduleConfig => !!moduleConfig?.moduleConfig),
        withLatestFrom(this.generalGateway$)
      )
      .subscribe(([moduleConfig, generalGateway]) => {
        const configModules = moduleConfig.moduleConfig?.uvsModule || [];
        if (configModules.length > 0) {
          const uvsModules: UvsModuleModel[] = [];
          for (const module of configModules) {
            const isOtm = module.typ === 'OTM01' || module.typ === 'OTM02' || module.typ === 'OTM03';
            const address = isOtm ? +module.address - 59 : +module.address;
            const existing = generalGateway.uvsModules.find(m => m.address === address);
            if (existing && isSameModuleType(existing.uvsType, module.typ)) {
              uvsModules.push(existing);
            } else {
              uvsModules.push({
                modulId: existing?.modulId ?? address + '',
                uvsType: module.typ as GatewayType,
                address,
                sensorParameters: {},
                measuredValues: {}
              } as UvsModuleModel);
            }
          }
          generalGateway.uvsModules = uvsModules.sort((a, b) => a.address - b.address);
        }
        for (const module of configModules) {
          if (module.typ === 'OTM01' || module.typ === 'OTM02' || module.typ === 'OTM03') {
            const index = generalGateway.uvsModules.findIndex(m => m.address === (module.address - 59));
            if (index != -1) {
              generalGateway.uvsModules[index].sensorTypeName = module.sensorTypeName;
              generalGateway.uvsModules[index].sensorParameters = module.sensorParameters;
            }
          }
        }
        this.generalGateway = generalGateway;
      }));

    this.subscriptions.push(this.deviceDataService.moduleConfig$
      .pipe(
        filter(moduleConfig => !!moduleConfig?.moduleConfig),
        skipUntil(this.sensorsCounter$.pipe(filter(sensors => !!sensors), take(1))),
        withLatestFrom(this.sensorsCounter$)
      )
      .subscribe(([moduleConfig, sensorsCounter]) => {
        const counterSensors = sensorsCounter.counterSensors;
        for (const module of moduleConfig.moduleConfig?.uvsModule) {
          if (!!module.irUartCfg && module.irUartCfg.length > 0) {
            for (const irUartCfg of module.irUartCfg) {
              const sensorIndex = counterSensors.findIndex(s => s.module === module.address && s.sensorId === irUartCfg.input);
              if (sensorIndex === -1) {
                counterSensors.push({
                  module: module.address,
                  sensorId: irUartCfg.input,
                  manufacturer: null,
                  name: '',
                  meterType: MeterTypeEnum.IR,
                  irType: irUartCfg.device as IRMeterTypeEnum,
                  seriesNumber: null
                })
              }
            }
          }
        }

        this.sensorsCounter.counterSensors = counterSensors;
      }));

    this.subscriptions.push(this.building$.subscribe(data => {
      this.autoSaveService.saveBuilding(data);
    }));

    this.subscriptions.push(this.generalGateway$.subscribe(data => {
      this.autoSaveService.saveGeneralGateway(data);
    }));

    this.subscriptions.push(this.generalMeasurement$.subscribe(data => {
      this.autoSaveService.saveGeneralMeasurement(data);
    }));

    this.subscriptions.push(this.generalPrePhotos$.subscribe(data => {
      this.autoSaveService.saveGeneralPrePhotos(data);
    }));

    this.subscriptions.push(this.generalSending$.subscribe(data => {
      this.autoSaveService.saveGeneralSending(data);
    }));

    this.subscriptions.push(this.heatingCheck$.subscribe(data => {
      this.autoSaveService.saveHeatingCheck(data);
    }));

    this.subscriptions.push(this.heatingInformation$.subscribe(data => {
      this.autoSaveService.saveHeatingInformation(data);
    }));

    this.subscriptions.push(this.photos$.subscribe(data => {
      this.autoSaveService.savePhotos(data);
    }));

    this.subscriptions.push(this.sensorsCounter$.subscribe(data => {
      this.autoSaveService.saveSensorsCounter(data);
    }));

    this.subscriptions.push(this.sensorsOther$.subscribe(data => {
      this.autoSaveService.saveSensorsOther(data);
    }));

    this.subscriptions.push(this.sensorsOutsideTemp$.subscribe(data => {
      this.autoSaveService.saveSensorsOutsideTemp(data);
    }));

    this.subscriptions.push(this.sensorsPump$.subscribe(data => {
      this.autoSaveService.saveSensorsPump(data);
    }));

    this.subscriptions.push(this.sensorsTemperature$.subscribe(data => {
      this.autoSaveService.saveSensorsTemperature(data);
    }));
  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  private buildGatewayLoadPipeline(gatewayId: string): Observable<void> {
    this.simIp = null;
    this.version = null;
    this.signalQuality = undefined;

    if (!gatewayId) {
      this.radioType = null;
      this.signalQuality = "0";
      return of(void 0);
    }

    if (this.isMqttBlocked) {
      return of(void 0);
    }

    this.deviceDataService.gatewayId = gatewayId;

    return this.dataService.loadDeviceInfo$(gatewayId).pipe(
      tap(deviceDto => {
        if (!deviceDto) {
          this.simIp = null;
          this.radioType = null;
          this.version = null;
          this.signalQuality = "0";
          this.alertService.error("Keine Daten für Gateway " + gatewayId + " gefunden");
        } else {
          this.simIp = deviceDto.readInfo.gatewayInfo.ip;
          this.radioType = deviceDto.readInfo.gatewayInfo.radioType;
          this.version = deviceDto.readInfo.versionApp;
          this.signalQuality = deviceDto.readInfo.gatewayInfo.snr;
          this.deviceDataService.setDevice(deviceDto);
        }
      }),
      switchMap(() => this.dataService.getGatewayBySerialNumber$(gatewayId).pipe(
        catchError(() => of(null as IInstallationDataResponse))
      )),
      tap((installationData: IInstallationDataResponse) => {
        this.deviceDataService.setInstallationData(installationData);
        if (installationData) {
          Utils.setDataModelsFromInstalledData(this, installationData);
        }
      }),
      switchMap(() => this.deviceDataService.reloadDeviceData$(gatewayId)),
      tap(() => Utils.initPumpSensorIds(this.deviceDataService.getDeviceData()?.deviceData)),
      delay(0),
      switchMap(() => this.deviceDataService.loadModuleConfig$(gatewayId)),
      map(() => void 0),
      catchError(e => {
        console.warn(e);
        return of(void 0);
      })
    );
  }

  async init() {
    const [
      building,
      generalGateway,
      generalMeasurement,
      generalPrePhotos,
      generalSending,
      heatingCheck,
      heatingInformation,
      photos,
      sensorsCounter,
      sensorsOther,
      sensorsOutsideTemp,
      sensorsPump,
      sensorsTemperature
    ] = await Promise.all([
      this.autoSaveService.getBuilding().then(data => data || new BuildingModel()),
      this.autoSaveService.getGeneralGateway().then(data => data || new GeneralGatewayModel()),
      this.autoSaveService.getGeneralMeasurement().then(data => data || new GeneralMeasurementModel()),
      this.autoSaveService.getGeneralPrePhotos().then(data => data || new GeneralPrePhotosModel()),
      this.autoSaveService.getGeneralSending().then(data => data || new GeneralSendingModel()),
      this.autoSaveService.getHeatingCheck().then(data => data || new HeatingCheckModel()),
      this.autoSaveService.getHeatingInformation().then(data => data || new HeatingInformationModel()),
      this.autoSaveService.getPhotos().then(data => data || new PhotosModel()),
      this.autoSaveService.getSensorsCounter().then(data => data || new SensorsCounterModel()),
      this.autoSaveService.getSensorsOther().then(data => data || new SensorsOtherModel()),
      this.autoSaveService.getSensorsOutsideTemp().then(data => data || new SensorsOutsideTempModel()),
      this.autoSaveService.getSensorsPump().then(data => data || new SensorsPumpModel()),
      this.autoSaveService.getSensorsTemperature().then(data => data || new SensorsTemperatureModel())
    ]);

    BuildingModel.initialize(building);
    GeneralGatewayModel.initialize(generalGateway);
    GeneralMeasurementModel.initialize(generalMeasurement);
    GeneralPrePhotosModel.initialize(generalPrePhotos);
    GeneralSendingModel.initialize(generalSending);
    HeatingCheckModel.initialize(heatingCheck);
    HeatingInformationModel.initialize(heatingInformation);
    PhotosModel.initialize(photos);
    SensorsCounterModel.initialize(sensorsCounter);
    SensorsOtherModel.initialize(sensorsOther);
    SensorsOutsideTempModel.initialize(sensorsOutsideTemp);
    SensorsPumpModel.initialize(sensorsPump);
    SensorsTemperatureModel.initialize(sensorsTemperature);

    this.buildingSubject.next(building);
    this.generalGatewaySubject.next(generalGateway);
    this.generalMeasurementSubject.next(generalMeasurement);
    this.generalPrePhotosSubject.next(generalPrePhotos);
    this.generalSendingSubject.next(generalSending);
    this.heatingCheckSubject.next(heatingCheck);
    this.heatingInformationSubject.next(heatingInformation);
    this.photosSubject.next(photos);
    this.sensorsCounterSubject.next(sensorsCounter);
    this.sensorsOtherSubject.next(sensorsOther);
    this.sensorsOutsideTempSubject.next(sensorsOutsideTemp);
    this.sensorsPumpSubject.next(sensorsPump);
    this.sensorsTemperatureSubject.next(sensorsTemperature);

    this.gatewayIdSubject.next(await this.autoSaveService.getGatewayId());
    this.deviceDataService.gatewayId = this.getGatewayId();

    const customerOptions = (await this.dataService.getCustomersWithAvailableSites()) as Customer[];
    customerOptions.sort((a, b) => a.customerName.localeCompare(b.customerName));
    this.customerOptionsSubject.next(customerOptions);
  }

  get building(): BuildingModel {
    return this.buildingSubject.value;
  }

  set building(data: BuildingModel) {
    this.buildingSubject.next(data);
  }

  get generalGateway(): GeneralGatewayModel {
    return this.generalGatewaySubject.value;
  }

  set generalGateway(data: GeneralGatewayModel) {
    this.generalGatewaySubject.next(data);
  }

  get generalMeasurement(): GeneralMeasurementModel {
    return this.generalMeasurementSubject.value;
  }

  set generalMeasurement(data: GeneralMeasurementModel) {
    this.generalMeasurementSubject.next(data);
  }

  get generalPrePhotos(): GeneralPrePhotosModel {
    return this.generalPrePhotosSubject.value;
  }

  set generalPrePhotos(data: GeneralPrePhotosModel) {
    this.generalPrePhotosSubject.next(data);
  }

  get generalSending(): GeneralSendingModel {
    return this.generalSendingSubject.value;
  }

  set generalSending(data: GeneralSendingModel) {
    this.generalSendingSubject.next(data);
  }

  get heatingCheck(): HeatingCheckModel {
    return this.heatingCheckSubject.value;
  }

  set heatingCheck(data: HeatingCheckModel) {
    this.heatingCheckSubject.next(data);
  }

  get heatingInformation(): HeatingInformationModel {
    return this.heatingInformationSubject.value;
  }

  set heatingInformation(data: HeatingInformationModel) {
    this.heatingInformationSubject.next(data);
  }

  get photos(): PhotosModel {
    return this.photosSubject.value;
  }

  set photos(data: PhotosModel) {
    this.photosSubject.next(data);
  }

  get sensorsCounter(): SensorsCounterModel {
    return this.sensorsCounterSubject.value;
  }

  set sensorsCounter(data: SensorsCounterModel) {
    this.sensorsCounterSubject.next(data);
  }

  get sensorsOther(): SensorsOtherModel {
    return this.sensorsOtherSubject.value;
  }

  set sensorsOther(data: SensorsOtherModel) {
    this.sensorsOtherSubject.next(data);
  }

  get sensorsOutsideTemp(): SensorsOutsideTempModel {
    return this.sensorsOutsideTempSubject.value;
  }

  set sensorsOutsideTemp(data: SensorsOutsideTempModel) {
    this.sensorsOutsideTempSubject.next(data);
  }

  get sensorsPump(): SensorsPumpModel {
    return this.sensorsPumpSubject.value;
  }

  set sensorsPump(data: SensorsPumpModel) {
    this.sensorsPumpSubject.next(data);
  }

  get sensorsTemperature(): SensorsTemperatureModel {
    return this.sensorsTemperatureSubject.value;
  }

  set sensorsTemperature(data: SensorsTemperatureModel) {
    this.sensorsTemperatureSubject.next(data);
  }

  getGatewayId(): string {
    return this.gatewayIdSubject.value;
  }

  async setGatewayId(gatewayId: string) {
    this.gatewayIdSubject.next(gatewayId);
    this.autoSaveService.saveGatewayId(gatewayId);
  }

  async setGatewayIdWithReset(gatewayId: string) {
    const contactPersonName = this.building.contactPersonName;
    const contactPersonNumber = this.building.contactPersonNumber;

    this.resetAllData();

    const building = this.building;
    building.contactPersonName = contactPersonName;
    building.contactPersonNumber = contactPersonNumber;
    this.building = building;

    this.setGatewayId(gatewayId);
    await this.router.navigate(['/gateway/general/gateway']);
  }

  get customerOptions(): Customer[] {
    return this.customerOptionsSubject.value;
  }

  set customerOptions(data: Customer[]) {
    this.customerOptionsSubject.next(data);
  }

  /**
   * this method also resets rootPage data if the gatewayId is not defined
   */
  public async checkSignalQuality() {
    if (this.isMqttBlocked) {
      return;
    }
    this.signalQuality = undefined;
    try {
      if (this.getGatewayId()) {
        let deviceDto = await this.dataService.loadDeviceInfo(this.getGatewayId());
        if (!deviceDto) {
          this.simIp = null;
          this.radioType = null;
          this.version = null;
          this.signalQuality = "0";
          this.alertService.error("Keine Daten für Gateway " + this.getGatewayId() + " gefunden");
        } else {
          this.simIp = deviceDto?.readInfo.gatewayInfo.ip;
          this.radioType = deviceDto?.readInfo.gatewayInfo.radioType;
          this.version = deviceDto?.readInfo.versionApp;
          this.signalQuality = deviceDto?.readInfo.gatewayInfo.snr;
          this.deviceDataService.setDevice(deviceDto);
        }
      } else {
        console.warn("No gatewayId provided");
        this.simIp = null;
        this.radioType = null;
        this.version = null;
        this.signalQuality = "0";
      }
    } catch (e) {
      console.warn(e);
      this.signalQuality = "0";
    }
  }

  private async loadInstallationData() {
    console.log('RootPageComponent.loadInstallationData serialNo: ', this.getGatewayId());
    if (this.getGatewayId()) {
      const installationData: IInstallationDataResponse = await this.dataService.getGatewayBySerialNumber(this.getGatewayId());
      this.deviceDataService.setInstallationData(installationData);
      if (installationData) {
        Utils.setDataModelsFromInstalledData(this, installationData);
      }
    }
  }

  public resetAllData() {
    this.buildingSubject.next(new BuildingModel());
    this.generalGatewaySubject.next(new GeneralGatewayModel());
    this.generalMeasurementSubject.next(new GeneralMeasurementModel());
    this.generalPrePhotosSubject.next(new GeneralPrePhotosModel());
    this.generalSendingSubject.next(new GeneralSendingModel());
    this.heatingCheckSubject.next(new HeatingCheckModel());
    this.heatingInformationSubject.next(new HeatingInformationModel());
    this.photosSubject.next(new PhotosModel());
    this.sensorsCounterSubject.next(new SensorsCounterModel());
    this.sensorsOtherSubject.next(new SensorsOtherModel());
    this.sensorsOutsideTempSubject.next(new SensorsOutsideTempModel());
    this.sensorsPumpSubject.next(new SensorsPumpModel());
    this.sensorsTemperatureSubject.next(new SensorsTemperatureModel());
  }

  public getDataForConsoleLog() {
    return {
      building: this.building,
      generalGateway: this.generalGateway,
      generalMeasurement: this.generalMeasurement,
      generalPrePhotos: this.generalPrePhotos,
      generalSending: this.generalSending,
      heatingCheck: this.heatingCheck,
      heatingInformation: this.heatingInformation,
      photos: this.photos,
      sensorsCounter: this.sensorsCounter,
      sensorsOther: this.sensorsOther,
      sensorsOutsideTemp: this.sensorsOutsideTemp,
      sensorsPump: this.sensorsPump,
      sensorsTemperature: this.sensorsTemperature
    }
  }
}
